import { useTranslation } from 'react-i18next';
import styles from './Datepicker.module.scss';
import classNames from 'classnames';
import { capitalizeFirstLetter } from '..';
import { ReactComponent as Arrow } from '../../../assets/schedule-arrow.svg';
import { DateTime, Interval } from 'luxon';
import { useState, useMemo } from 'react';

interface DatepickerProps {
  selectedDate: DateTime | null;
  setSelectedDate: (date: DateTime) => void;
  clearDate?: () => void;
  timezone?: string;
  start?: DateTime;
  end?: DateTime;
  locale?: string;
}

/*
TODO:
1. Отключение конкретных дат
2. Отключение дней недели
3. Унифицировать сравнение дат без приведения к миллисекундам
4. Убрать пересчёт отключенных дат при смене даты
5. Адаптировать под Date на входе
*/

/**
 * Независимый и настраиваемый компонент календаря
 * @param selectedDate выбранная дата (это может быть внешний стейт компонента или redux-стейт)
 * @param setSelectedDate метод установки выбранной даты во внешнем приложении
 * @param timezone часовой пояс
 * @param start начальная дата календаря
 * @param end конечная дата календаря
 * @returns JSX-элемент календаря
 */
export default function Datepicker({
  selectedDate,
  setSelectedDate,
  clearDate,
  timezone = 'Europe/Moscow',
  start,
  end,
  locale = 'ru',
}: Readonly<DatepickerProps>): JSX.Element {
  const { t } = useTranslation();

  const today = DateTime.now().setZone(timezone).startOf('day');
  const [selectedMonth, setSelectedMonth] = useState<DateTime>(
    selectedDate
      ? selectedDate.setZone(timezone).startOf('month')
      : today.startOf('month')
  );

  const calendarMonth = useMemo(() => {
    const startOfCalendarMonth = selectedMonth.startOf('week');
    const endOfCalendarMonth = selectedMonth.endOf('month').endOf('week');

    const calendarMonth = Interval.fromDateTimes(
      startOfCalendarMonth,
      endOfCalendarMonth
    )
      .splitBy({ week: 1 })
      .map(week => week.splitBy({ day: 1 }).map(day => day.start));

    return calendarMonth;
  }, [selectedMonth.month]);

  /**
   * Переключает дату вперёд или назад на 1 месяц и выставляет 1 число месяца
   * @param decrement переключить на предыдущий месяц
   */
  const changeMonth = (options?: { decrement?: boolean }) => {
    const newMonth = selectedMonth
      .plus({ month: options?.decrement ? -1 : 1 })
      .startOf('month');

    setSelectedMonth(newMonth);
  };

  return (
    <div className={styles.root}>
      {clearDate && (
        <button
          className={styles.clearButton}
          onClick={clearDate}
        >
          {t('clear')}
        </button>
      )}
      <div className={styles.monthPicker}>
        <button
          type='button'
          className={styles.arrowWrapper}
          disabled={
            selectedMonth.get('year') ==
              (start ? start.get('year') : today.get('year')) &&
            selectedMonth.get('month') <=
              (start ? start.get('month') : today.get('month'))
          }
          onClick={() => changeMonth({ decrement: true })}
        >
          <Arrow className={classNames(styles.arrow, styles.left)} />
        </button>
        <h3 className={styles.month}>
          {capitalizeFirstLetter(
            selectedMonth.setLocale(locale).toFormat('LLLL')
          )}
        </h3>
        <button
          type='button'
          className={styles.arrowWrapper}
          disabled={
            end
              ? selectedMonth.get('year') === end.get('year') &&
                selectedMonth.get('month') === end.get('month')
              : false
          }
          onClick={() => changeMonth()}
        >
          <Arrow className={styles.arrow} />
        </button>
      </div>
      <table className={styles.datepicker}>
        <thead>
          <tr className={styles.daysOfWeek}>
            {[...Array(7)].map((_, i) => {
              const weekDay = t<string, string[]>('weekdays', {
                returnObjects: true,
              })[i];

              return (
                <th
                  key={weekDay}
                  className={classNames(styles.day, styles.dayOfWeek)}
                >
                  {weekDay}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {calendarMonth.map(week => (
            <tr key={`${week[0].day}_${week[0].month}`}>
              {week.map(day => {
                const disabled =
                  day < today || (start && day < start) || (end && day > end);
                const selected = selectedDate
                  ? +day === +selectedDate.startOf('day')
                  : false;

                return (
                  <td
                    key={`${day.month}-${day.day}`}
                    className={styles.day}
                  >
                    <button
                      className={classNames(
                        styles.dayButton,
                        selected && styles.selected
                      )}
                      disabled={disabled}
                      onClick={() => setSelectedDate(day)}
                    >
                      {day.day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
